import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Archive, Building, ChevronDown, Coins, Download, FileText, LogOut, User,
} from 'lucide-react';
import { apiService } from '../services/api.service';
import { LanguageMenu } from './LanguageSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Le menu de compte de l'app bar, au nom de l'organisation consultée.
 *
 * Il rassemble ce qui concerne l'ORGANISATION et pas l'événement choisi :
 * ses réglages, ses jetons, sa facturation, ses archives. Avant, ces entrées
 * étaient éparpillées entre deux badges de l'app bar (dont un masqué à zéro)
 * et une rangée d'icônes sans libellé au bas de la barre latérale.
 *
 * Un admin qui consulte une organisation voit son nom sur le bouton : c'est
 * le repère « je suis chez BodyMind ».
 */
interface AccountMenuProps {
  organizationName: string | null;
  email?: string;
  isParticipant: boolean;
  billingEnabled: boolean;
  onLogout: () => void;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export function AccountMenu({
  organizationName, email, isParticipant, billingEnabled, onLogout,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Le solde est lu à l'ouverture du menu : c'est là qu'on le regarde, et ça
  // évite un sondage permanent pour un chiffre caché la plupart du temps.
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);

  const hasOrganization = !isParticipant && !!organizationName;

  const refreshBalance = useCallback(async () => {
    if (!hasOrganization) return;
    try {
      const res = await apiService.getTokenBalance();
      setTokenBalance(res.token_balance ?? 0);
    } catch {
      // Le solde n'est qu'une indication : l'entrée « Jetons » reste
      // utilisable et sa page dira ce qu'il en est.
      setTokenBalance(null);
    }
  }, [hasOrganization]);

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) refreshBalance(); }}>
      <DropdownMenuTrigger asChild>
        <button
          data-tour="account-menu"
          aria-label={t('nav.userMenu')}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted"
        >
          {hasOrganization ? (
            <>
              <span className="glass-ring flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-foreground">
                {initials(organizationName!)}
              </span>
              <span className="hidden max-w-[180px] truncate text-sm font-medium md:block">
                {organizationName}
              </span>
            </>
          ) : (
            <User size={18} className="text-foreground" />
          )}
          <ChevronDown size={14} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          {hasOrganization && (
            <div className="truncate text-sm font-medium text-foreground">{organizationName}</div>
          )}
          <div className="truncate text-xs text-muted-foreground">{email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {hasOrganization && (
          <>
            <DropdownMenuItem onClick={() => navigate('/admin/settings')}>
              <Building size={16} className="mr-2 text-muted-foreground" />
              {t('nav.organizationSettings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/admin/my-license')}>
              <Coins size={16} className="mr-2 text-muted-foreground" />
              {t('nav.tokens')}
              {tokenBalance !== null && (
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {tokenBalance}
                </span>
              )}
            </DropdownMenuItem>
            {billingEnabled && (
              <DropdownMenuItem onClick={() => navigate('/admin/statements')}>
                <FileText size={16} className="mr-2 text-muted-foreground" />
                {t('nav.statements')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate('/admin/archives')}>
              <Archive size={16} className="mr-2 text-muted-foreground" />
              {t('nav.archives')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {!isParticipant && (
          <DropdownMenuItem onClick={() => navigate('/admin/downloads')}>
            <Download size={16} className="mr-2 text-muted-foreground" />
            {t('nav.downloadOrganizer')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => navigate(isParticipant ? '/participant-dashboard' : '/admin/account')}
        >
          <User size={16} className="mr-2 text-muted-foreground" />
          {t('nav.myProfile')}
        </DropdownMenuItem>
        <LanguageMenu />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout}>
          <LogOut size={16} className="mr-2 text-muted-foreground" />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AccountMenu;
